import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  input,
  output
} from '@angular/core';

import { ICONS } from '@gfe/core/services/icons/icons.service';

import { ButtonComponent, ButtonType } from '@shared/components/button/button.component';

export enum DrawerSize {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
  FULL = 'full'
}

export interface DrawerAction {
  label: string;
  callback: () => void;
  disabled?: boolean;
  /** Icon class, e.g. `ICONS.plus`. */
  icon?: string;
  color: ButtonType;
}

export interface DrawerConfig {
  /** Icon class, e.g. `ICONS.truck` or `EntityIconService.getEntityIcon(type)`. */
  icon?: string;
  /** Buttons rendered at the right end of the footer bar. */
  actions?: DrawerAction[];
  /** Renders the close button in the header. */
  dismissible?: boolean;
  closeOnBackdropClick?: boolean;
  /**
   * Defaults to `dismissible`. Set to `false` when the drawer hosts its own
   * Escape-dismissible overlays (dropdowns, filter popups) and has to decide itself
   * whether an Escape belongs to the drawer or to what it contains.
   */
  closeOnEscape?: boolean;
}

/**
 * Presentational shell for a modal side drawer: backdrop, sliding panel, header and footer
 * chrome. Callers own visibility (`@if`) and fill the `drawer-header`, `drawer-body` and
 * `drawer-footer` slots; the header and footer bars hide themselves when nothing lands in
 * them.
 */
@Component({
  selector: 'app-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent],
  templateUrl: './drawer.component.html',
  styleUrl: './drawer.component.css'
})
export class DrawerComponent {
  title = input<string>('');
  config = input<DrawerConfig>({});
  size = input<DrawerSize>(DrawerSize.MD);
  zIndex = input<number>(40);

  closeDrawer = output<void>();

  panelClass = computed(() => {
    switch (this.size()) {
      case DrawerSize.FULL:
        return 'sm:max-w-[92vw]';
      case DrawerSize.XL:
        return 'sm:max-w-6xl';
      case DrawerSize.LG:
        return 'sm:max-w-4xl';
      case DrawerSize.SM:
        return 'sm:max-w-md';
      case DrawerSize.MD:
      default:
        return 'sm:max-w-2xl';
    }
  });

  protected readonly ICONS = ICONS;

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    const config = this.config();
    if (config.closeOnEscape ?? config.dismissible) {
      this.closeDrawer.emit();
    }
  }

  protected onBackdropClick(): void {
    if (this.config().closeOnBackdropClick) {
      this.closeDrawer.emit();
    }
  }
}
